import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import { getTask, getTasks, TaskResponse, TaskStatus } from 'api/tasks'
import { SyncProductsResponse } from 'api/products'

const formatDateTime = (value?: string) => {
  if (!value) return '-'
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const statusColor: Record<
  TaskStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error'
}

const isSyncProductsResponse = (
  result: TaskResponse['result']
): result is SyncProductsResponse =>
  Boolean(
    result &&
      typeof result === 'object' &&
      'updatedCount' in result &&
      'createdCount' in result
  )

const getSummaryTotal = (task: TaskResponse) => {
  const result = isSyncProductsResponse(task.result) ? task.result : null
  const createdCount = result?.createdCount ?? 0
  const updatedCount = result?.updatedCount ?? 0
  return createdCount + updatedCount
}

const TasksPage = () => {
  const [tasks, setTasks] = useState<TaskResponse[]>([])
  const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null)
  const [taskIdInput, setTaskIdInput] = useState('')
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [isLoadingTask, setIsLoadingTask] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasRunningTasks = useMemo(
    () => tasks.some(task => task.status === 'running'),
    [tasks]
  )

  const fetchTask = async (taskId: string) => {
    if (!taskId.trim()) return
    setIsLoadingTask(true)
    setError(null)
    try {
      const task = await getTask(taskId.trim())
      setSelectedTask(task)
      setTaskIdInput(task.id)
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load task'
      )
    } finally {
      setIsLoadingTask(false)
    }
  }

  const fetchTasks = async () => {
    setIsLoadingTasks(true)
    setError(null)
    try {
      const response = await getTasks({ limit: 50 })
      setTasks(response.tasks)
      if (!selectedTask && response.tasks.length > 0) {
        await fetchTask(response.tasks[0].id)
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load tasks'
      )
    } finally {
      setIsLoadingTasks(false)
    }
  }

  useEffect(() => {
    fetchTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!hasRunningTasks) return undefined

    const intervalId = window.setInterval(() => {
      fetchTasks()
      if (selectedTask?.status === 'running') {
        fetchTask(selectedTask.id)
      }
    }, 5000)

    return () => window.clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRunningTasks, selectedTask?.id, selectedTask?.status])

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentTurnedInIcon fontSize='large' color='primary' />
          <Box>
            <Typography variant='h4'>Tasks</Typography>
            <Typography variant='body2' color='text.secondary'>
              Delayed jobs and NetSuite task results
            </Typography>
          </Box>
        </Box>

        <Paper variant='outlined' sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
          >
            <TextField
              label='Task ID'
              value={taskIdInput}
              onChange={event => setTaskIdInput(event.target.value)}
              size='small'
              sx={{ flex: 1 }}
            />
            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={() => fetchTask(taskIdInput)}
              disabled={isLoadingTask || !taskIdInput.trim()}
            >
              Search
            </Button>
            <Button
              variant='outlined'
              startIcon={<RefreshIcon />}
              onClick={fetchTasks}
              disabled={isLoadingTasks}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity='error'>{error}</Alert>}

        <Paper variant='outlined'>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='h6'>Recent Tasks</Typography>
            {isLoadingTasks && <CircularProgress size={18} />}
          </Box>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align='right'>Created</TableCell>
                  <TableCell align='right'>Updated</TableCell>
                  <TableCell align='right'>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color='text.secondary'>
                        No tasks found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {tasks.map(task => {
                  const syncResult = isSyncProductsResponse(task.result)
                    ? task.result
                    : null

                  return (
                    <TableRow
                      key={task.id}
                      hover
                      selected={selectedTask?.id === task.id}
                      onClick={() => fetchTask(task.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography
                          variant='body2'
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {task.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{task.type}</TableCell>
                      <TableCell>
                        <Chip
                          label={task.status}
                          size='small'
                          color={statusColor[task.status]}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDateTime(task.startedAt ?? undefined)}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(task.completedAt ?? undefined)}
                      </TableCell>
                      <TableCell align='right'>
                        {syncResult?.createdCount ?? '-'}
                      </TableCell>
                      <TableCell align='right'>
                        {syncResult?.updatedCount ?? '-'}
                      </TableCell>
                      <TableCell align='right'>
                        {syncResult ? getSummaryTotal(task) : '-'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper variant='outlined' sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant='h6'>Task Details</Typography>
            {isLoadingTask && <CircularProgress size={18} />}
          </Box>

          {!selectedTask ? (
            <Typography color='text.secondary'>
              Select a task or search by task ID.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip label={selectedTask.id} variant='outlined' />
                <Chip label={selectedTask.type} variant='outlined' />
                <Chip
                  label={selectedTask.status}
                  color={statusColor[selectedTask.status]}
                />
                <Chip
                  label={`Started ${formatDateTime(
                    selectedTask.startedAt ?? undefined
                  )}`}
                />
                <Chip
                  label={`Completed ${formatDateTime(
                    selectedTask.completedAt ?? undefined
                  )}`}
                />
              </Stack>

              {selectedTask.error && (
                <Alert severity='error'>{selectedTask.error}</Alert>
              )}

              {selectedTask.result &&
                isSyncProductsResponse(selectedTask.result) && (
                  <>
                    <Stack
                      direction='row'
                      spacing={1}
                      sx={{ flexWrap: 'wrap' }}
                    >
                      <Chip
                        label={`${
                          selectedTask.result.createdCount ?? 0
                        } created`}
                        color={
                          (selectedTask.result.createdCount ?? 0) > 0
                            ? 'success'
                            : 'default'
                        }
                      />
                      <Chip
                        label={`${
                          selectedTask.result.updatedCount ?? 0
                        } updated`}
                        color={
                          (selectedTask.result.updatedCount ?? 0) > 0
                            ? 'primary'
                            : 'default'
                        }
                      />
                      {selectedTask.result.productLookupDataSync && (
                        <>
                          <Chip
                            variant='outlined'
                            label={`Collections: ${selectedTask.result.productLookupDataSync.collections.created} created, ${selectedTask.result.productLookupDataSync.collections.updated} updated, ${selectedTask.result.productLookupDataSync.collections.deactivated} inactive`}
                          />
                          <Chip
                            variant='outlined'
                            label={`Colors: ${
                              selectedTask.result.productLookupDataSync.colors
                                .created
                            } created, ${
                              selectedTask.result.productLookupDataSync.colors
                                .updated
                            } updated, ${
                              selectedTask.result.productLookupDataSync.colors
                                .deactivated
                            } inactive, ${
                              selectedTask.result.productLookupDataSync.colors
                                .skipped ?? 0
                            } skipped`}
                          />
                        </>
                      )}
                    </Stack>

                    <Divider />

                    <TableContainer sx={{ maxHeight: 520 }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>SKU</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Changes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTask.result.updates.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <Typography color='text.secondary'>
                                  No products changed.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedTask.result.updates.map((item, index) => (
                              <TableRow key={`${item.itemName}-${index}`}>
                                <TableCell>{item.itemName}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.changeType}
                                    size='small'
                                    color={
                                      item.changeType === 'created'
                                        ? 'success'
                                        : 'primary'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.5}>
                                    {Object.entries(item.changes).map(
                                      ([field, value]) => (
                                        <Typography
                                          key={field}
                                          variant='body2'
                                          sx={{ fontFamily: 'monospace' }}
                                        >
                                          <strong>{field}:</strong>{' '}
                                          {String(value.from)} {'->'}{' '}
                                          {String(value.to)}
                                        </Typography>
                                      )
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

              {selectedTask.result &&
                !isSyncProductsResponse(selectedTask.result) && (
                  <Paper variant='outlined' sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography
                      component='pre'
                      variant='body2'
                      sx={{
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {JSON.stringify(selectedTask.result, null, 2)}
                    </Typography>
                  </Paper>
                )}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  )
}

export default TasksPage
